import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch, connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Button, H3, LayoutSingleColumn, NamedLink, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './CartPage.module.css';
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { cardRenderSizes, groupListingsByAuthor, isArrayLength } from '../../util/genericHelpers';
import CartPanel from './CartPanel';
import { initializeCardPaymentData } from '../../ducks/stripe.duck';
import { handleSubmit } from '../ListingPage/ListingPage.shared';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createSlug } from '../../util/urlHelpers';
import { extractListingDataForCart } from '../../util/cartHelpers';
import { currentPageResultIdsSelector } from './CartPage.duck';
import { currentUserSelector } from '../../ducks/user.duck';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { useConfiguration } from '../../context/configurationContext';

const { Money } = sdkTypes;

const CartPage = () => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  // Redux state using useSelector
  const currentUser = useSelector(currentUserSelector);
  const currentPageResultIds = useSelector(currentPageResultIdsSelector);
  const listings = useSelector(state => getListingsById(state, currentPageResultIds));
  const combinedListings = Object.values(groupListingsByAuthor(listings));
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const marketplaceCurrency = config.currency;
  // Local state for stock details
  const [stockDetails, setStockDetails] = useState([]);

  // Fetch and update stock details when listings change
  useEffect(() => {
    if (isArrayLength(listings)) {
      const listingDetails = listings.map(extractListingDataForCart);
      setStockDetails(listingDetails);
    } else {
      setStockDetails([]);
    }
  }, [listings.length]);

  // Extract listings once using useSelector
  const allListings = useSelector(state =>
    getMarketplaceEntities(state, currentPageResultIds.map(id => ({ id, type: 'listing' })))
  );

  // Safe helper function to find a listing by ID
  const getListing = id =>
    (isArrayLength(allListings) && allListings.find(l => l.id.uuid === id?.uuid)) || null;
  // Handle order submission
  const handleOrderSubmit = async combinedListings => {
    if (!combinedListings?.length) return;

    const listing = combinedListings[0];
    const author = listing.author;
    const listingParams = {
      id: listing.id.uuid,
      slug: createSlug(listing.attributes.title),
    };
    const cartItems = stockDetails.filter(item => item?.authorId === author?.id?.uuid);
    await handleSubmit({
      params: listingParams,
      history,
      routes: routeConfiguration,
      currentUser,
      getListing,
      callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
        dispatch(setInitialValues(values, saveToSessionStorage)),
      onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
      cartItems,
    })({ quantity: 1, deliveryMethod: 'shipping' });
  };

  const title = intl.formatMessage({ id: 'CartPage.title' });

  const calculateSubtotal = items =>
    formatMoney(
      intl,
      new Money(
        items.reduce((acc, item) => {
          const lineTotal =
            item?.price?.amount && item?.stockCount ? item.price.amount * item.stockCount : 0;
          return acc + lineTotal;
        }, 0),
        marketplaceCurrency
      )
    );

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.fixWidthContainer}>
          <div className={css.headings}>
            <H3 as="h1" className={css.title}>
              {intl.formatMessage({ id: 'CartPage.heading' })}
            </H3>
            <NamedLink className={css.continuebtn} name="SearchPage">
              {intl.formatMessage({ id: 'CartPage.continueShopping' })}
            </NamedLink>
          </div>
          <div className={css.listingCards}>
            {isArrayLength(combinedListings) ? (
              combinedListings.map((cl, i) => (
                <div className={css.vendor} key={i}>
                  <div className={css.venderCards}>
                    {cl.map(l => (
                      <CartPanel
                        className={css.listingCard}
                        key={l?.id?.uuid}
                        listing={l}
                        renderSizes={cardRenderSizes(false)}
                        currentUser={currentUser}
                        intl={intl}
                        stockDetails={stockDetails}
                        setStockDetails={setStockDetails}
                        currentCartItem={
                          isArrayLength(stockDetails)
                            ? {
                                item: stockDetails.find(sd => sd.listingId === l?.id?.uuid),
                                index: stockDetails.findIndex(sd => sd.listingId === l?.id?.uuid),
                              }
                            : { item: null, index: null }
                        }
                      />
                    ))}
                  </div>
                  <div className={css.checkoutButton}>
                    <div className={css.checkoutDetails}>
                      <div className={css.totalPrice}>
                        <p>{cl?.map(l => l?.attributes?.title).join(', ')}</p>
                      </div>
                      <div className={css.totalItems}>
                        <p>
                          {intl.formatMessage({ id: 'CartPage.totalItems' }, { count: cl.length })}
                        </p>
                      </div>
                      <div className={css.totalPrice}>
                        <p>{intl.formatMessage({ id: 'CartPage.subtotal' })}</p>
                        <h3>
                          {calculateSubtotal(
                            stockDetails.filter(sd => cl.find(l => l.id.uuid === sd.listingId))
                          )}
                        </h3>
                      </div>
                    </div>
                    <Button onClick={() => handleOrderSubmit(cl)}>
                      {intl.formatMessage({ id: 'CartPage.checkout' })}
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div>{intl.formatMessage({ id: 'CartPage.emptyCart' })}</div>
            )}
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default CartPage;
